import { tz } from '@date-fns/tz';
import { format as fnsFormat, parseISO } from 'date-fns';
import parsePhoneNumber from 'libphonenumber-js';

import { ApiSchemas } from '@/components/providers/v1-api';

export function formatPhoneNumber(phoneNumber?: string | null) {
  if (!phoneNumber) return '';
  const phone = parsePhoneNumber(phoneNumber);
  if (phone?.isValid()) return phone.formatNational();
  return phoneNumber;
}

export function formatJson(json: string) {
  try {
    const obj = JSON.parse(json);
    return JSON.stringify(obj, null, 2);
  } catch {
    return json;
  }
}

export function formatAddress(location: ApiSchemas['LocationRead']) {
  let address = '';
  if (location.line1) {
    address += `${location.line1}`;
  }
  if (location.line2) {
    address += `, ${location.line2}`;
  }
  if (location.city) {
    address += `, ${location.city}`;
  }
  if (location.state) {
    address += `, ${location.state} ${location.zip}`.trim();
  }
  return address;
}

export function formatDateTimeInUTC(date: Date | string, format: string) {
  if (typeof date === 'string') {
    const d = parseISO(date);
    return fnsFormat(d, format, { in: tz('UTC') });
  }
  return fnsFormat(date, format, { in: tz('UTC') });
}

export function formatDurationFromMillis(value: number, unit: 'sec' | 'min' = 'sec') {
  if (!Number.isFinite(value) || value < 0) return '';
  const totalMillis = Math.max(0, Math.floor(value));
  const totalSeconds = Math.floor(totalMillis / 1000);

  if (unit === 'sec') {
    // Render total seconds with unit
    return `${totalSeconds} s`;
  }

  // unit === 'min' -> render total minutes with 2 decimal places
  const totalMinutes = (totalSeconds / 60).toFixed(2);
  return `${totalMinutes} m`;
}

export function formatMetersAsMiles(meters: number) {
  return `${(meters * 0.000621371).toFixed(1)}`;
}

export function formatTravelTime(seconds: number): string {
  const minutes = Math.round(seconds / 60);
  // round up to next 5-minute increment
  const ceiledTo5 = Math.ceil(minutes / 5) * 5;
  return `${ceiledTo5} min`;
}

export function formatCustomerName(customer: {
  company?: string | null;
  first_name?: string | null;
  last_name?: string | null;
}) {
  if (customer.company && customer.company !== '') {
    return customer.company;
  }
  const name = `${customer.first_name ?? ''} ${customer.last_name ?? ''}`.trim();
  if (name === '') {
    return 'No name';
  }
  return name;
}
